import React, { useRef, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./Header.css";
import "./dropdown.css";
import { fetchJson } from "../../api";
import { forgetCachedLogo, useSiteSettings } from "../../SiteSettings";

const Header = () => {
  const { logoUrl, logoLoaded } = useSiteSettings();
  const location = useLocation();
  const navbarRef = useRef(null);
  const servicesRef = useRef(null);
  const aboutRef = useRef(null);

  const [menuServices, setMenuServices] = useState([]);
  // A logo can disappear server-side (replaced/deleted) — never keep showing
  // a broken image; fall back to the brand name instead.
  const [logoBroken, setLogoBroken] = useState(false);

  useEffect(() => {
    setLogoBroken(false);
  }, [logoUrl]);

  const showLogo = Boolean(logoUrl) && !logoBroken;

  useEffect(() => {
    let mounted = true;

    fetchJson("services").then((data) => {
      if (!mounted || !data || !Array.isArray(data.services)) return;

      const items = data.services.map((s) => ({
        id: s.id,
        name: s.name,
        icon: s.icon || "🏥",
        desc: s.description || "",
        route_path: s.route_path || "/services",
        title: s.name,
      }));

      setMenuServices(items);
    });

    return () => {
      mounted = false;
    };
  }, []);

  const isActive = (path) => location.pathname === path;
  const isServicesActive = () => location.pathname.startsWith("/services");

  const closeMobileMenu = () => {
    const navbar = navbarRef.current;
    if (!navbar) return;

    if (navbar.classList.contains("show")) {
      navbar.classList.remove("show");

      const toggler = navbar.parentElement?.querySelector(".navbar-toggler");
      if (toggler) {
        toggler.setAttribute("aria-expanded", "false");
        toggler.classList.add("collapsed");
      }
    }
  };

  const closeDropdown = (dropdownRef) => {
    const dropdown = dropdownRef?.current;
    if (!dropdown) return;

    const toggle = dropdown.querySelector(".dropdown-toggle");
    const menu = dropdown.querySelector(".dropdown-menu");

    dropdown.classList.add("dropdown-closing");
    dropdown.classList.remove("show");
    menu?.classList.remove("show");
    toggle?.classList.remove("show");
    toggle?.setAttribute("aria-expanded", "false");
    toggle?.blur();

    if (window.bootstrap?.Dropdown && toggle) {
      const instance = window.bootstrap.Dropdown.getOrCreateInstance(toggle);
      instance.hide();
    }

    window.setTimeout(() => {
      dropdown.classList.remove("dropdown-closing");
    }, 250);
  };

  const closeAllDropdowns = () => {
    closeDropdown(servicesRef);
    closeDropdown(aboutRef);
  };

  const handleNavClick = (dropdownRef = null) => {
    closeMobileMenu();

    if (dropdownRef) {
      closeDropdown(dropdownRef);
    } else {
      closeAllDropdowns();
    }
  };

  useEffect(() => {
    closeMobileMenu();
    closeAllDropdowns();
  }, [location.pathname]);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SiteNavigationElement",
            name: [
              "Home",
              "About Us",
              "IVF Services",
              "Contact",
              "Book Appointment"
            ],
            url: [
              "https://amaanativf.com/",
              "https://amaanativf.com/about",
              "https://amaanativf.com/services",
              "https://amaanativf.com/contact",
              "https://amaanativf.com/bookappointment"
            ]
          })}
        </script>
      </Helmet>

      <div className="top-header">
        <div className="marquee-banner">
          <div className="marquee">
            <div className="marquee-content">
              🎉 Free IVF Consultation This Week | 20% Discount on First Cycle |
              Limited Seats Available | Call Now!
            </div>
          </div>
        </div>

        <nav className="navbar navbar-expand-lg bg-white shadow-sm" role="navigation">
          <div className="container">
            <Link
              className="navbar-brand d-flex align-items-center"
              to="/"
              title="Amaanat IVF Clinic Home"
              onClick={() => handleNavClick()}
            >
              {/* The logo comes only from Settings. Nothing else is ever
                  rendered in its place, so the header never swaps a
                  fallback logo for the configured one. */}
              {showLogo ? (
                <img
                  src={logoUrl}
                  alt="Amaanat IVF Clinic Logo - Best IVF Centre in Ambala"
                  className="navbar-brand-img"
                  loading="eager"
                  onError={() => {
                    forgetCachedLogo();
                    setLogoBroken(true);
                  }}
                />
              ) : logoLoaded ? (
                <span className="navbar-brand-text">Amaanat IVF</span>
              ) : (
                <span className="navbar-brand-placeholder" aria-hidden="true" />
              )}
            </Link>

            <button
              className="navbar-toggler collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarMenu"
              aria-controls="navbarMenu"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            <div
              className="collapse navbar-collapse"
              id="navbarMenu"
              ref={navbarRef}
            >
              <ul className="navbar-nav ms-auto align-items-lg-center">
                <li className="nav-item">
                  <Link
                    className={`nav-link ${isActive("/") ? "nav-link-active" : ""}`}
                    to="/"
                    title="Home - IVF Clinic in Ambala"
                    onClick={() => handleNavClick()}
                  >
                    Home
                  </Link>
                </li>

                <li className="nav-item dropdown" ref={servicesRef}>
                  <a
                    className={`nav-link dropdown-toggle ${
                      isServicesActive() ? "fw-bold nav-link-active" : ""
                    }`}
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="true"
                    aria-expanded="false"
                    title="IVF and Fertility Services"
                    onClick={(e) => e.preventDefault()}
                  >
                    Services
                  </a>

                  <ul className="dropdown-menu dropdown-menu-custom services-mega">
                    {menuServices.map((s) => (
                      <li key={s.id}>
                        <Link
                          className={`dropdown-item service-item ${
                            location.pathname === s.route_path ? "active" : ""
                          }`}
                          to={s.route_path}
                          title={s.title}
                          onClick={() => handleNavClick(servicesRef)}
                        >
                          <span className="service-item-icon" aria-hidden="true">
                            {s.icon}
                          </span>
                          <span className="service-item-name">{s.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>

                <li className="nav-item dropdown" ref={aboutRef}>
                  <a
                    className={`nav-link dropdown-toggle ${
                      isActive("/about") || isActive("/doctors")
                        ? "fw-bold nav-link-active"
                        : ""
                    }`}
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="true"
                    aria-expanded="false"
                    title="About Amaanat IVF"
                    onClick={(e) => e.preventDefault()}
                  >
                    About Us
                  </a>

                  <ul className="dropdown-menu dropdown-menu-custom">
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/about"
                        title="Our Founders"
                        onClick={() => handleNavClick(aboutRef)}
                      >
                        Our Founders
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/doctors"
                        title="Our Experts"
                        onClick={() => handleNavClick(aboutRef)}
                      >
                        Our Experts
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to="/blogs"
                        title="Blog & Articles"
                        onClick={() => handleNavClick(aboutRef)}
                      >
                        Blogs
                      </Link>
                    </li>
                  </ul>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${isActive("/contact") ? "nav-link-active" : ""}`}
                    to="/contact"
                    title="Contact IVF Clinic in Ambala"
                    onClick={() => handleNavClick()}
                  >
                    Contact
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${isActive("/career") ? "nav-link-active" : ""}`}
                    to="/career"
                    title="Careers at Amaanat IVF"
                    onClick={() => handleNavClick()}
                  >
                    Careers
                  </Link>
                </li>

                <li className="nav-item ms-lg-3">
                  <Link
                    to="/bookappointment"
                    className="btn btn-appointment"
                    title="Book IVF Appointment"
                    onClick={() => handleNavClick()}
                  >
                    Book Appointment
                  </Link>
                </li>

                <li className="nav-item ms-lg-3">
                  <a
                    className="btn btn-appointmentt"
                    href="tel:9991641100"
                    title="Call IVF Clinic"
                    onClick={() => handleNavClick()}
                  >
                    Call Us : 9991641100
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Header;